import { useState, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2, LocateFixed } from "lucide-react";

const NOMINATIM_URL = "https://nominatim.openstreetmap.org";

export default function GeoLocationInput({ label, value, onChange, required, placeholder, testId }) {
  const [locating, setLocating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);

  const handleLocateMe = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch(
            `${NOMINATIM_URL}/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&addressdetails=1&accept-language=it`,
            { headers: { "User-Agent": "ColucciAssicuratori/1.0" } }
          );
          const data = await res.json();
          if (data.display_name) {
            onChange(data.display_name);
          }
        } catch (err) {
          console.error("Geolocation reverse failed:", err.message);
        } finally {
          setLocating(false);
        }
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [onChange]);

  const handleSearch = useCallback((query) => {
    onChange(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${NOMINATIM_URL}/search?format=json&q=${encodeURIComponent(query)}&countrycodes=it&limit=5&addressdetails=1&accept-language=it`,
          { headers: { "User-Agent": "ColucciAssicuratori/1.0" } }
        );
        const data = await res.json();
        setSuggestions(data.map(d => d.display_name));
        setShowSuggestions(data.length > 0);
      } catch (err) {
        console.error("Geocoding search failed:", err.message);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 400);
  }, [onChange]);

  const selectSuggestion = (s) => {
    onChange(s);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="space-y-2 relative" data-testid={testId}>
      <Label className="text-sm text-[#0F1F14]">
        {label || "Sede Legale o Residenza"} {required && <span className="text-[#D97B58]">*</span>}
      </Label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5E51]" />
        <Input
          value={value || ""}
          onChange={e => handleSearch(e.target.value)}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          className="rounded-xl border-[#E5E2DB] h-11 pl-9 pr-10 focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321]"
          placeholder={placeholder || "Inizia a digitare l'indirizzo..."}
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={handleLocateMe}
          disabled={locating}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-[#4A5E51] hover:text-[#163321] hover:bg-[#163321]/5 transition-all disabled:opacity-50"
          title="Usa la mia posizione"
          data-testid={`${testId}-locate-btn`}
        >
          {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
        </button>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-[#E5E2DB] shadow-lg overflow-hidden" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s, i) => (
            <button
              key={`${s.slice(0, 30)}-${i}`}
              type="button"
              onMouseDown={() => selectSuggestion(s)}
              className="w-full text-left px-4 py-2.5 text-sm text-[#0F1F14] hover:bg-[#F5F4F0] transition-colors flex items-start gap-2 border-b border-[#E5E2DB]/50 last:border-0"
              data-testid={`${testId}-suggestion-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 text-[#4A5E51] mt-0.5 shrink-0" />
              <span className="leading-tight">{s}</span>
            </button>
          ))}
        </div>
      )}

      <p className="text-[10px] text-[#4A5E51]/60">
        Digita l'indirizzo o usa il pulsante di geolocalizzazione per rilevare la tua posizione
      </p>
    </div>
  );
}
